import {
  Actions,
  Alert,
  Button,
  Card,
  Divider,
  Heading,
  IconAdd,
  Loader,
  Notice,
  Stack,
  Text,
} from 'braid-design-system';
import React, { useState } from 'react';
import { SmartTextLink } from 'scoobie';

import type {
  GetPartnerTokenPayload,
  LiveClientPayload,
  PublicTestClientPayload,
} from '#src/api/index.ts';
import { usePermissions } from '#src/hooks/auth.tsx';
import { useSelf } from '#src/hooks/self.ts';

import { useCredentials } from '../../CredentialsContext.tsx';
import { Credential } from '../Credential/Credential.tsx';
import { DeleteCredentialDialog } from '../DeleteCredentialDialog/DeleteCredentialDialog.tsx';
import { NewCredentialDialog } from '../NewCredentialDialog/NewCredentialDialog.tsx';
import { PartnerTokenDialog } from '../partnerTokenDialog.tsx';

export const CredentialList = () => {
  const { permissions, isInternalTestPartner } = usePermissions();
  const hasWriteAccess = permissions.includes('mutate:credentials');
  const hasReadAccess = permissions.includes('query:credentials');

  const { self } = useSelf();
  const canSelfService = Boolean(
    self?.partner.credentialSelfServiceApprovedIndicator,
  );

  const { loading, error, liveCredentials, publicTestCredentials, refresh } =
    useCredentials();

  const [clientToDelete, setClientToDelete] = useState<
    LiveClientPayload | PublicTestClientPayload
  >();

  const [partnerToken, setPartnerToken] = useState<GetPartnerTokenPayload>();

  const [newDataType, setNewDataType] = useState<'live' | 'publicTest'>();

  if (loading || !self) {
    return <Loader />;
  }

  if (error) {
    return (
      <Alert tone="critical">
        <Stack space="small">
          <Text>We couldn’t load your SEEK API credentials.</Text>
          <Text size="small">{error}</Text>
        </Stack>
      </Alert>
    );
  }

  return (
    <>
      <Stack space="xlarge">
        <Card>
          <Stack space="large">
            <Heading level="3">Live credentials</Heading>

            <Stack space="medium">
              {liveCredentials.length ? (
                liveCredentials.map((credential, i) => (
                  <React.Fragment key={credential.clientId}>
                    {i > 0 && <Divider />}

                    <Credential
                      credential={credential}
                      onDeleteRequest={() => setClientToDelete(credential)}
                      onGeneratePartnerToken={(generatedPartnerToken) =>
                        setPartnerToken(generatedPartnerToken)
                      }
                      writeAccess={canSelfService && hasWriteAccess}
                    />
                  </React.Fragment>
                ))
              ) : (
                <Text tone="secondary">No credentials found.</Text>
              )}

              {canSelfService && hasWriteAccess ? (
                <>
                  <Divider />

                  {liveCredentials.length === 2 ? (
                    <Notice tone="info">
                      <Text>
                        You can have 2 sets of live credentials active at a
                        time, to facilitate zero-downtime credential rotation.
                        If you need to generate a new set of credentials, delete
                        an existing one first.
                      </Text>
                    </Notice>
                  ) : (
                    <Actions>
                      <Button
                        icon={<IconAdd />}
                        onClick={() => setNewDataType('live')}
                        variant="soft"
                      >
                        New live credential
                      </Button>
                    </Actions>
                  )}
                </>
              ) : null}

              {!canSelfService ? (
                <>
                  <Divider />

                  <Notice tone="info">
                    <Text>
                      You haven’t been approved to access live credentials.
                      Review our{' '}
                      <SmartTextLink href="/introduction/development-and-debugging#development-process">
                        development process
                      </SmartTextLink>{' '}
                      and contact SEEK when you’re ready to proceed.
                    </Text>
                  </Notice>
                </>
              ) : null}
            </Stack>
          </Stack>
        </Card>

        <Card>
          <Stack space="large">
            <Heading level="3">Playground credentials</Heading>
            <Stack space="medium">
              {publicTestCredentials.length ? (
                publicTestCredentials.map((credential, i) => (
                  <React.Fragment key={credential.clientId}>
                    {i > 0 && <Divider />}

                    <Credential
                      credential={credential}
                      onDeleteRequest={() => setClientToDelete(credential)}
                      onGeneratePartnerToken={(generatedPartnerToken) =>
                        setPartnerToken(generatedPartnerToken)
                      }
                      writeAccess={hasWriteAccess}
                    />
                  </React.Fragment>
                ))
              ) : (
                <Text tone="secondary">No credentials found.</Text>
              )}

              {hasWriteAccess ? (
                <>
                  <Divider />

                  {publicTestCredentials.length &&
                  // Allow unlimited Playground credentials to be generated under
                  // our internal organizations.
                  !isInternalTestPartner ? (
                    <Notice tone="info">
                      <Text>
                        You can have 1 set of Playground credentials active at a
                        time. If you need to generate a new set of credentials,
                        delete your existing one first.
                      </Text>
                    </Notice>
                  ) : (
                    <Actions>
                      <Button
                        icon={<IconAdd />}
                        onClick={() => setNewDataType('publicTest')}
                        variant="soft"
                      >
                        New playground credential
                      </Button>
                    </Actions>
                  )}
                </>
              ) : null}
            </Stack>
          </Stack>
        </Card>

        {newDataType && hasWriteAccess ? (
          <NewCredentialDialog
            dataType={newDataType}
            id="new-credential-dialog"
            open={true}
            onClose={() => setNewDataType(undefined)}
            onCreate={refresh}
          />
        ) : null}
      </Stack>

      {partnerToken && hasReadAccess ? (
        <PartnerTokenDialog
          partnerToken={partnerToken}
          onClose={() => setPartnerToken(undefined)}
        />
      ) : null}

      {clientToDelete && hasWriteAccess ? (
        <DeleteCredentialDialog
          client={clientToDelete}
          liveClientCount={liveCredentials.length}
          open={true}
          onClose={() => setClientToDelete(undefined)}
          onDelete={refresh}
        />
      ) : null}
    </>
  );
};
